import { useEffect, useMemo, useState } from "react";
import api from "../../services/api";

const WIZARDS = [
  { key: "bedrijf", label: "Bedrijfssetup" },
  { key: "team", label: "Team & rechten" },
  { key: "project", label: "Project & klant" },
];

const DEFAULT_BEDRIJF_FORM = {
  timezone: "Europe/Amsterdam",
  requireExecutionLog: true,
  requireHours: true,
  requireMaterials: false,
  requireEvidence: true,
  allowMonteurCreate: true,
  allowMonteurEditOwn: true,
  allowMonteurCloseOwn: true,
};

const DEFAULT_MONTEUR_FORM = {
  naam: "",
  email: "",
  wachtwoord: "",
  personeelsnummer: "",
  maxHoursPerWeek: "40",
  canCreateWorkorders: false,
};

const DEFAULT_CLIENT_FORM = {
  naam: "",
  email: "",
  telefoon: "",
  plaats: "",
};

const DEFAULT_PROJECT_FORM = {
  naam: "",
  projectNummer: "",
  locationText: "",
  startDate: "",
  clientId: "",
};

function WizardHeader({ title, subtitle, step, totalSteps }) {
  return (
    <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
          Stap {step} van {totalSteps}
        </span>
      </div>
      <p className="text-sm text-slate-600">{subtitle}</p>
      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-100">
        <div
          className="h-full bg-slate-700 transition-all"
          style={{ width: `${Math.round((step / totalSteps) * 100)}%` }}
        />
      </div>
    </div>
  );
}

function BedrijfssetupWizard() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState(DEFAULT_BEDRIJF_FORM);

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        setLoading(true);
        const res = await api.get("/tenant/settings");
        if (!active) return;
        const settings = res?.data || {};
        setForm({
          timezone: String(settings?.rateResolution?.timezone || "Europe/Amsterdam"),
          requireExecutionLog: settings?.completionRequirements?.requireExecutionLog !== false,
          requireHours: settings?.completionRequirements?.requireHours !== false,
          requireMaterials: settings?.completionRequirements?.requireMaterials === true,
          requireEvidence: settings?.completionRequirements?.requireEvidence !== false,
          allowMonteurCreate: settings?.actionPolicies?.allowMonteurCreate !== false,
          allowMonteurEditOwn: settings?.actionPolicies?.allowMonteurEditOwn !== false,
          allowMonteurCloseOwn: settings?.actionPolicies?.allowMonteurCloseOwn !== false,
        });
      } catch (err) {
        if (!active) return;
        setError(err?.response?.data?.message || "Bedrijfssetup laden mislukt");
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, []);

  async function saveSettings() {
    try {
      setSaving(true);
      setError("");
      setSuccess("");
      await Promise.all([
        api.put("/tenant/settings/rate-resolution", {
          timezone: form.timezone,
        }),
        api.put("/tenant/settings/completion-requirements", {
          requireExecutionLog: form.requireExecutionLog,
          requireHours: form.requireHours,
          requireMaterials: form.requireMaterials,
          requireEvidence: form.requireEvidence,
        }),
        api.put("/tenant/settings/action-policies", {
          allowMonteurCreate: form.allowMonteurCreate,
          allowMonteurEditOwn: form.allowMonteurEditOwn,
          allowMonteurCloseOwn: form.allowMonteurCloseOwn,
        }),
      ]);
      setSuccess("Bedrijfssetup opgeslagen.");
      setStep(3);
    } catch (err) {
      setError(err?.response?.data?.message || "Opslaan van bedrijfssetup mislukt");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <p className="text-sm text-slate-500">Bedrijfssetup laden...</p>;
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      {step === 1 && (
        <>
          <WizardHeader
            title="Bedrijfssetup wizard"
            subtitle="Zet de basisinstellingen voor planning en afronding."
            step={1}
            totalSteps={3}
          />
          <div className="grid gap-3 md:grid-cols-2">
            <label className="text-sm text-slate-700">
              Tijdzone
              <input
                value={form.timezone}
                onChange={(e) => setForm((prev) => ({ ...prev, timezone: e.target.value }))}
                className="mt-1 w-full rounded border border-slate-300 bg-white p-2"
              />
            </label>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={() => setStep(2)}
              className="rounded bg-slate-800 px-3 py-2 text-sm font-medium text-white hover:bg-slate-900"
            >
              Volgende
            </button>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <WizardHeader
            title="Bedrijfssetup wizard"
            subtitle="Bepaal verplichte afronding en monteur-actierechten."
            step={2}
            totalSteps={3}
          />
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded border border-slate-200 bg-white p-3">
              <p className="mb-2 text-sm font-semibold text-slate-900">Afrondingseisen</p>
              <Toggle
                label="Daglog verplicht"
                checked={form.requireExecutionLog}
                onChange={(value) =>
                  setForm((prev) => ({ ...prev, requireExecutionLog: value }))
                }
              />
              <Toggle
                label="Uren verplicht"
                checked={form.requireHours}
                onChange={(value) => setForm((prev) => ({ ...prev, requireHours: value }))}
              />
              <Toggle
                label="Materiaal verplicht"
                checked={form.requireMaterials}
                onChange={(value) =>
                  setForm((prev) => ({ ...prev, requireMaterials: value }))
                }
              />
              <Toggle
                label="Foto/bewijs verplicht"
                checked={form.requireEvidence}
                onChange={(value) =>
                  setForm((prev) => ({ ...prev, requireEvidence: value }))
                }
              />
            </div>
            <div className="rounded border border-slate-200 bg-white p-3">
              <p className="mb-2 text-sm font-semibold text-slate-900">Monteur-acties</p>
              <Toggle
                label="Monteur mag actie aanmaken"
                checked={form.allowMonteurCreate}
                onChange={(value) =>
                  setForm((prev) => ({ ...prev, allowMonteurCreate: value }))
                }
              />
              <Toggle
                label="Monteur mag eigen actie bewerken"
                checked={form.allowMonteurEditOwn}
                onChange={(value) =>
                  setForm((prev) => ({ ...prev, allowMonteurEditOwn: value }))
                }
              />
              <Toggle
                label="Monteur mag eigen actie sluiten"
                checked={form.allowMonteurCloseOwn}
                onChange={(value) =>
                  setForm((prev) => ({ ...prev, allowMonteurCloseOwn: value }))
                }
              />
            </div>
          </div>

          {error ? <p className="mt-3 text-sm text-red-700">{error}</p> : null}
          {success ? <p className="mt-3 text-sm text-emerald-700">{success}</p> : null}

          <div className="mt-4 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setStep(1)}
              className="rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700"
            >
              Vorige
            </button>
            <button
              type="button"
              onClick={saveSettings}
              disabled={saving}
              className="rounded bg-slate-800 px-3 py-2 text-sm font-medium text-white hover:bg-slate-900 disabled:opacity-60"
            >
              {saving ? "Opslaan..." : "Opslaan en afronden"}
            </button>
          </div>
        </>
      )}

      {step === 3 && (
        <>
          <WizardHeader
            title="Bedrijfssetup wizard"
            subtitle="Klaar. De basisinstellingen staan goed voor nieuwe klanten."
            step={3}
            totalSteps={3}
          />
          <div className="rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
            Bedrijfssetup is opgeslagen.
          </div>
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={() => setStep(1)}
              className="rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700"
            >
              Opnieuw openen
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function TeamWizard() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState(DEFAULT_MONTEUR_FORM);
  const [createdTempPassword, setCreatedTempPassword] = useState("");
  const [users, setUsers] = useState([]);

  async function loadUsers() {
    try {
      setLoading(true);
      const res = await api.get("/users?role=Monteur");
      const list = Array.isArray(res?.data?.users)
        ? res.data.users
        : Array.isArray(res?.data)
        ? res.data
        : [];
      setUsers(list);
    } catch (err) {
      setError(err?.response?.data?.message || "Monteurs laden mislukt");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadUsers();
  }, []);

  async function createTechnician() {
    try {
      if (!String(form.naam || "").trim() || !String(form.email || "").trim()) {
        setError("Naam en e-mail zijn verplicht.");
        return;
      }

      setSaving(true);
      setError("");
      setSuccess("");

      const payload = {
        naam: String(form.naam || "").trim(),
        email: String(form.email || "").trim(),
        ...(String(form.wachtwoord || "").trim()
          ? { wachtwoord: String(form.wachtwoord).trim() }
          : {}),
        ...(String(form.personeelsnummer || "").trim()
          ? { personeelsnummer: String(form.personeelsnummer).trim() }
          : {}),
        technicianProfile: {
          maxHoursPerWeek: Number(form.maxHoursPerWeek || 40),
          canCreateWorkorders: form.canCreateWorkorders === true,
        },
      };

      const res = await api.post("/users/technicians", payload);
      setCreatedTempPassword(String(res?.data?.user?.temporaryPassword || "").trim());
      setSuccess("Monteur aangemaakt.");
      setForm(DEFAULT_MONTEUR_FORM);
      await loadUsers();
      setStep(2);
    } catch (err) {
      setError(err?.response?.data?.message || "Monteur aanmaken mislukt");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <p className="text-sm text-slate-500">Teamwizard laden...</p>;
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      {step === 1 && (
        <>
          <WizardHeader
            title="Team & rechten wizard"
            subtitle="Maak de eerste monteur aan."
            step={1}
            totalSteps={3}
          />
          <div className="grid gap-3 md:grid-cols-2">
            <label className="text-sm text-slate-700">
              Naam
              <input
                value={form.naam}
                onChange={(e) => setForm((prev) => ({ ...prev, naam: e.target.value }))}
                className="mt-1 w-full rounded border border-slate-300 bg-white p-2"
              />
            </label>
            <label className="text-sm text-slate-700">
              E-mail
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                className="mt-1 w-full rounded border border-slate-300 bg-white p-2"
              />
            </label>
            <label className="text-sm text-slate-700">
              Wachtwoord (optioneel)
              <input
                value={form.wachtwoord}
                onChange={(e) => setForm((prev) => ({ ...prev, wachtwoord: e.target.value }))}
                className="mt-1 w-full rounded border border-slate-300 bg-white p-2"
              />
            </label>
            <label className="text-sm text-slate-700">
              Personeelsnummer
              <input
                value={form.personeelsnummer}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, personeelsnummer: e.target.value }))
                }
                className="mt-1 w-full rounded border border-slate-300 bg-white p-2"
              />
            </label>
            <label className="text-sm text-slate-700">
              Max uren per week
              <input
                type="number"
                min="0"
                max="168"
                value={form.maxHoursPerWeek}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, maxHoursPerWeek: e.target.value }))
                }
                className="mt-1 w-full rounded border border-slate-300 bg-white p-2"
              />
            </label>
            <label className="mt-6 flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.canCreateWorkorders}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, canCreateWorkorders: e.target.checked }))
                }
              />
              Mag werkbonnen aanmaken
            </label>
          </div>
          {error ? <p className="mt-3 text-sm text-red-700">{error}</p> : null}
          {success ? <p className="mt-3 text-sm text-emerald-700">{success}</p> : null}
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={createTechnician}
              disabled={saving}
              className="rounded bg-slate-800 px-3 py-2 text-sm font-medium text-white hover:bg-slate-900 disabled:opacity-60"
            >
              {saving ? "Aanmaken..." : "Monteur aanmaken"}
            </button>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <WizardHeader
            title="Team & rechten wizard"
            subtitle="Controleer je teamlijst."
            step={2}
            totalSteps={3}
          />
          <div className="rounded border border-slate-200 bg-white p-3">
            <p className="mb-2 text-sm font-semibold text-slate-900">Monteurs in tenant</p>
            {!users.length ? (
              <p className="text-sm text-slate-500">Nog geen monteurs gevonden.</p>
            ) : (
              <ul className="space-y-1 text-sm text-slate-700">
                {users.slice(0, 10).map((user) => (
                  <li key={String(user?._id || user?.email || Math.random())}>
                    {user?.naam || "-"} - {user?.email || "-"}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {createdTempPassword ? (
            <p className="mt-3 text-sm text-amber-700">
              Tijdelijk wachtwoord voor laatst aangemaakte monteur:{" "}
              <strong>{createdTempPassword}</strong>
            </p>
          ) : null}
          <div className="mt-4 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setStep(1)}
              className="rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700"
            >
              Vorige
            </button>
            <button
              type="button"
              onClick={() => setStep(3)}
              className="rounded bg-slate-800 px-3 py-2 text-sm font-medium text-white hover:bg-slate-900"
            >
              Volgende
            </button>
          </div>
        </>
      )}

      {step === 3 && (
        <>
          <WizardHeader
            title="Team & rechten wizard"
            subtitle="Klaar. Je team is ingericht voor de eerste planning."
            step={3}
            totalSteps={3}
          />
          <div className="rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
            Teamwizard afgerond.
          </div>
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={() => setStep(1)}
              className="rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700"
            >
              Nieuwe monteur toevoegen
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function ProjectWizard() {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [loadingClients, setLoadingClients] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [clientForm, setClientForm] = useState(DEFAULT_CLIENT_FORM);
  const [projectForm, setProjectForm] = useState(DEFAULT_PROJECT_FORM);
  const [clients, setClients] = useState([]);

  const selectedClientName = useMemo(() => {
    const found = clients.find((item) => String(item?._id || "") === String(projectForm.clientId || ""));
    return found?.naam || "";
  }, [clients, projectForm.clientId]);

  async function loadClients() {
    try {
      setLoadingClients(true);
      const res = await api.get("/clients");
      const list = Array.isArray(res?.data) ? res.data : [];
      setClients(list);
    } catch (err) {
      setError(err?.response?.data?.message || "Klanten laden mislukt");
    } finally {
      setLoadingClients(false);
    }
  }

  useEffect(() => {
    loadClients();
  }, []);

  async function createClientAndContinue() {
    try {
      if (!String(clientForm.naam || "").trim()) {
        setError("Klantnaam is verplicht.");
        return;
      }

      setSaving(true);
      setError("");
      setSuccess("");

      const payload = {
        naam: String(clientForm.naam || "").trim(),
        email: String(clientForm.email || "").trim(),
        telefoon: String(clientForm.telefoon || "").trim(),
        plaats: String(clientForm.plaats || "").trim(),
      };

      const res = await api.post("/clients", payload);
      const clientId = String(res?.data?._id || "").trim();
      setSuccess("Klant aangemaakt.");
      await loadClients();
      setProjectForm((prev) => ({ ...prev, clientId }));
      setStep(2);
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || "Klant aanmaken mislukt");
    } finally {
      setSaving(false);
    }
  }

  async function createProjectAndFinish() {
    try {
      if (!String(projectForm.naam || "").trim()) {
        setError("Projectnaam is verplicht.");
        return;
      }
      if (!String(projectForm.projectNummer || "").trim()) {
        setError("Projectnummer is verplicht.");
        return;
      }
      if (!String(projectForm.clientId || "").trim()) {
        setError("Koppel een klant aan het project.");
        return;
      }

      setSaving(true);
      setError("");
      setSuccess("");
      await api.post("/projects", {
        naam: String(projectForm.naam || "").trim(),
        projectNummer: String(projectForm.projectNummer || "").trim(),
        clientId: String(projectForm.clientId || "").trim(),
        locationText: String(projectForm.locationText || "").trim(),
        startDate: String(projectForm.startDate || "").trim() || null,
      });
      setSuccess("Project aangemaakt.");
      setStep(3);
    } catch (err) {
      setError(err?.response?.data?.message || "Project aanmaken mislukt");
    } finally {
      setSaving(false);
    }
  }

  if (loadingClients) {
    return <p className="text-sm text-slate-500">Projectwizard laden...</p>;
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      {step === 1 && (
        <>
          <WizardHeader
            title="Project & klant wizard"
            subtitle="Maak eerst een klant aan."
            step={1}
            totalSteps={3}
          />
          <div className="grid gap-3 md:grid-cols-2">
            <label className="text-sm text-slate-700">
              Klantnaam
              <input
                value={clientForm.naam}
                onChange={(e) => setClientForm((prev) => ({ ...prev, naam: e.target.value }))}
                className="mt-1 w-full rounded border border-slate-300 bg-white p-2"
              />
            </label>
            <label className="text-sm text-slate-700">
              E-mail
              <input
                value={clientForm.email}
                onChange={(e) => setClientForm((prev) => ({ ...prev, email: e.target.value }))}
                className="mt-1 w-full rounded border border-slate-300 bg-white p-2"
              />
            </label>
            <label className="text-sm text-slate-700">
              Telefoon
              <input
                value={clientForm.telefoon}
                onChange={(e) =>
                  setClientForm((prev) => ({ ...prev, telefoon: e.target.value }))
                }
                className="mt-1 w-full rounded border border-slate-300 bg-white p-2"
              />
            </label>
            <label className="text-sm text-slate-700">
              Plaats
              <input
                value={clientForm.plaats}
                onChange={(e) => setClientForm((prev) => ({ ...prev, plaats: e.target.value }))}
                className="mt-1 w-full rounded border border-slate-300 bg-white p-2"
              />
            </label>
          </div>
          {error ? <p className="mt-3 text-sm text-red-700">{error}</p> : null}
          {success ? <p className="mt-3 text-sm text-emerald-700">{success}</p> : null}
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={createClientAndContinue}
              disabled={saving}
              className="rounded bg-slate-800 px-3 py-2 text-sm font-medium text-white hover:bg-slate-900 disabled:opacity-60"
            >
              {saving ? "Aanmaken..." : "Klant opslaan en verder"}
            </button>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <WizardHeader
            title="Project & klant wizard"
            subtitle="Maak nu een project met projectnummer."
            step={2}
            totalSteps={3}
          />
          <div className="grid gap-3 md:grid-cols-2">
            <label className="text-sm text-slate-700">
              Projectnaam
              <input
                value={projectForm.naam}
                onChange={(e) => setProjectForm((prev) => ({ ...prev, naam: e.target.value }))}
                className="mt-1 w-full rounded border border-slate-300 bg-white p-2"
              />
            </label>
            <label className="text-sm text-slate-700">
              Projectnummer
              <input
                value={projectForm.projectNummer}
                onChange={(e) =>
                  setProjectForm((prev) => ({ ...prev, projectNummer: e.target.value }))
                }
                className="mt-1 w-full rounded border border-slate-300 bg-white p-2"
              />
            </label>
            <label className="text-sm text-slate-700">
              Locatie
              <input
                value={projectForm.locationText}
                onChange={(e) =>
                  setProjectForm((prev) => ({ ...prev, locationText: e.target.value }))
                }
                className="mt-1 w-full rounded border border-slate-300 bg-white p-2"
              />
            </label>
            <label className="text-sm text-slate-700">
              Startdatum
              <input
                type="date"
                value={projectForm.startDate}
                onChange={(e) =>
                  setProjectForm((prev) => ({ ...prev, startDate: e.target.value }))
                }
                className="mt-1 w-full rounded border border-slate-300 bg-white p-2"
              />
            </label>
            <label className="text-sm text-slate-700 md:col-span-2">
              Klant
              <select
                value={projectForm.clientId}
                onChange={(e) => setProjectForm((prev) => ({ ...prev, clientId: e.target.value }))}
                className="mt-1 w-full rounded border border-slate-300 bg-white p-2"
              >
                <option value="">Selecteer klant</option>
                {clients.map((client) => (
                  <option key={client._id} value={client._id}>
                    {client.naam}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {error ? <p className="mt-3 text-sm text-red-700">{error}</p> : null}
          {success ? <p className="mt-3 text-sm text-emerald-700">{success}</p> : null}
          <div className="mt-4 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setStep(1)}
              className="rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700"
            >
              Vorige
            </button>
            <button
              type="button"
              onClick={createProjectAndFinish}
              disabled={saving}
              className="rounded bg-slate-800 px-3 py-2 text-sm font-medium text-white hover:bg-slate-900 disabled:opacity-60"
            >
              {saving ? "Aanmaken..." : "Project opslaan"}
            </button>
          </div>
        </>
      )}

      {step === 3 && (
        <>
          <WizardHeader
            title="Project & klant wizard"
            subtitle="Klaar. De basisrelaties zijn aangemaakt."
            step={3}
            totalSteps={3}
          />
          <div className="rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
            Projectwizard afgerond voor klant: <strong>{selectedClientName || "-"}</strong>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={() => setStep(1)}
              className="rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700"
            >
              Nieuwe klant/project starten
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function Toggle({ label, checked, onChange }) {
  return (
    <label className="mb-1 flex items-center justify-between gap-2 rounded border border-slate-100 p-2 text-sm text-slate-700">
      <span>{label}</span>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    </label>
  );
}

export default function OnboardingWizardsPage() {
  const [activeWizard, setActiveWizard] = useState("bedrijf");

  return (
    <section className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <h1 className="text-xl font-semibold text-slate-900">Onboarding Wizards</h1>
        <p className="mt-1 text-sm text-slate-600">
          Snelle setup voor nieuwe klanten: bedrijfsbasis, team en projectnummer-flow.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {WIZARDS.map((wizard) => (
          <button
            key={wizard.key}
            type="button"
            onClick={() => setActiveWizard(wizard.key)}
            className={`rounded-full border px-3 py-2 text-sm ${
              activeWizard === wizard.key
                ? "border-slate-800 bg-slate-800 text-white"
                : "border-slate-300 bg-white text-slate-700"
            }`}
          >
            {wizard.label}
          </button>
        ))}
      </div>

      {activeWizard === "bedrijf" && <BedrijfssetupWizard />}
      {activeWizard === "team" && <TeamWizard />}
      {activeWizard === "project" && <ProjectWizard />}
    </section>
  );
}
